import {
  Menu as HUIMenu,
  MenuButton as HUIMenuButton,
  MenuItem as HUIMenuItem,
  MenuItems as HUIMenuItems,
  Transition,
} from '@headlessui/react';
import { cn } from '../../../lib/styles';
import { Icon } from '../icon/icon.spark';
import type { MenuAlign, MenuProps } from './types';

const ALIGN_MAP: Record<MenuAlign, string> = {
  start: 'origin-top-left rtl:origin-top-right',
  end: 'origin-top-right rtl:origin-top-left',
} as const;

export function Menu({ align = 'start', ...props }: MenuProps) {
  const hasIcons = props.items.some((item) => item.icon);

  return (
    <HUIMenu as="div" className="text-sm text-spark-text-primary">
      {({ open }) => (
        <>
          <HUIMenuButton
            aria-label={props.collapsible ? props.buttonText : undefined}
            disabled={props.disabled}
            className={cn(
              'flex h-spark-component shrink-0 items-center rounded-lg border border-gray-300 bg-white shadow-xs transition hover:bg-gray-100 focus:outline-hidden focus:ring-2 focus:ring-spark-purple focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-spark-text-secondary disabled:opacity-50',
              props.collapsible
                ? 'w-spark-component justify-center px-0 sm:w-fit sm:justify-start sm:gap-2 sm:ps-3 sm:pe-2.5'
                : 'gap-2 ps-3 pe-2.5',
            )}
          >
            {props.collapsible ? (
              <>
                <span className="sm:hidden">
                  <Icon name="ellipsis" size="md" />
                </span>
                <span className="hidden sm:flex sm:items-center sm:gap-2">
                  {props.buttonText}
                  <Icon name="chevronDown" />
                </span>
              </>
            ) : (
              <>
                {props.buttonText}
                <Icon name="chevronDown" />
              </>
            )}
          </HUIMenuButton>
          <Transition
            show={open}
            enter="transform transition duration-100 ease-out"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="transform transition duration-100 ease-in"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <HUIMenuItems
              className={cn(
                'z-50 min-w-44 overflow-clip rounded-lg border border-gray-300 bg-white p-1 text-sm text-spark-text-primary shadow-lg [--anchor-gap:4px] focus:outline-hidden focus:ring-none',
                ALIGN_MAP[align],
              )}
              anchor={`bottom ${align}`}
            >
              {props.items.map((item) => (
                <HUIMenuItem key={item.key ?? item.label} disabled={item.disabled}>
                  <button
                    className={cn(
                      'group flex w-full items-center gap-3 rounded-md px-3 py-2 transition disabled:text-spark-text-secondary',
                      item.danger ? 'text-red-700 data-focus:bg-red-100' : 'data-focus:bg-gray-100',
                      item.disabled && 'cursor-not-allowed',
                    )}
                    disabled={item.disabled}
                    onClick={item.onClick}
                    type="button"
                  >
                    {hasIcons && (
                      <span
                        aria-hidden
                        className="inline-flex size-4.5 shrink-0 items-center justify-center"
                      >
                        {item.icon && <Icon name={item.icon} size="md" />}
                      </span>
                    )}
                    <span>{item.label}</span>
                  </button>
                </HUIMenuItem>
              ))}
            </HUIMenuItems>
          </Transition>
        </>
      )}
    </HUIMenu>
  );
}
